<template>
    <div>
        <!-- Payment Methods -->
        <section class="mb-8">
            <h3
                class="text-base font-semibold text-slate-900 dark:text-slate-100 mb-3"
            >
                Payment methods
            </h3>
            <div
                v-if="
                    paymentMethods && paymentMethods.length > 0
                "
                class="flex flex-col"
            >
                <div
                    v-for="(method, index) in paymentMethods"
                    :key="method.id"
                    class="flex items-center justify-between py-3"
                    :class="{
                        'border-b border-slate-100':
                            index < paymentMethods.length - 1,
                    }"
                >
                    <div class="flex items-center gap-3">
                        <div
                            class="w-10 h-7 bg-slate-100 dark:bg-slate-700 rounded flex items-center justify-center"
                        >
                            <span
                                class="text-xs font-bold text-slate-600 dark:text-slate-300"
                            >
                                {{
                                    cardBrandLabel(
                                        method.card?.brand
                                    )
                                }}
                            </span>
                        </div>
                        <div>
                            <div
                                class="flex items-center gap-2"
                            >
                                <span
                                    class="text-sm font-medium text-slate-900 dark:text-slate-100"
                                >
                                    {{
                                        cardBrandLabel(
                                            method.card?.brand
                                        )
                                    }}
                                    ({{ method.card?.last4 }})
                                </span>
                                <span
                                    v-if="
                                        method.id ===
                                        defaultPaymentMethodId
                                    "
                                    class="text-xs text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700 rounded-full px-2 py-0.5"
                                >
                                    Default
                                </span>
                            </div>
                            <span
                                class="text-xs text-slate-500 dark:text-slate-400"
                            >
                                Exp
                                {{
                                    String(
                                        method.card?.exp_month
                                    ).padStart(2, '0')
                                }}/{{
                                    String(
                                        method.card?.exp_year
                                    ).slice(-2)
                                }}
                            </span>
                        </div>
                    </div>
                    <div class="relative" data-payment-menu>
                        <button
                            class="p-2 text-slate-400 hover:text-slate-600 dark:text-slate-300 rounded"
                            @click="
                                togglePaymentMenu(method.id)
                            "
                        >
                            <svg
                                class="w-5 h-5"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <circle cx="4" cy="10" r="2" />
                                <circle cx="10" cy="10" r="2" />
                                <circle cx="16" cy="10" r="2" />
                            </svg>
                        </button>
                        <!-- Dropdown Menu -->
                        <div
                            v-if="
                                openPaymentMenuId === method.id
                            "
                            class="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-10 py-1"
                        >
                            <button
                                v-if="
                                    method.id !==
                                    defaultPaymentMethodId
                                "
                                class="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40 transition-colors"
                                @click="
                                    () => { openPaymentMenuId = null; $emit('set-default', method.id); }
                                "
                            >
                                <svg
                                    class="w-4 h-4 text-slate-400"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"
                                    />
                                    <path
                                        d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"
                                    />
                                </svg>
                                Make default
                            </button>
                            <button
                                class="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40 transition-colors"
                                @click="
                                    () => { openPaymentMenuId = null; $emit('edit-card', method); }
                                "
                            >
                                <svg
                                    class="w-4 h-4 text-slate-400"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"
                                    />
                                </svg>
                                Edit
                            </button>
                            <button
                                class="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                @click="
                                    () => { openPaymentMenuId = null; $emit('delete-card', method); }
                                "
                            >
                                <svg
                                    class="w-4 h-4"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                                Remove card
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <button
                class="flex items-center gap-2 text-sm font-medium text-slate-800 dark:text-slate-100 mt-3 hover:text-10x-orange transition-colors"
                @click="$emit('add-card')"
            >
                <span class="text-lg leading-none">+</span>
                Add payment method
            </button>
        </section>

        <!-- Billing Details -->
        <section class="mb-8">
            <h3
                class="text-base font-semibold text-slate-900 dark:text-slate-100 mb-3"
            >
                Billing details
            </h3>
            <div class="flex flex-col gap-4">
                <!-- Billing Address -->
                <div
                    class="flex items-start justify-between pt-2"
                >
                    <div class="flex flex-col gap-1">
                        <p
                            class="text-sm font-medium text-slate-900 dark:text-slate-100"
                        >
                            Billing address
                        </p>
                        <template v-if="billingAddress.name">
                            <p class="text-sm text-slate-500 dark:text-slate-400">
                                {{ billingAddress.name }}
                            </p>
                            <p
                                class="text-sm text-slate-500 dark:text-slate-400"
                                v-if="billingAddress.country"
                            >
                                {{ billingAddress.country }}
                            </p>
                            <p
                                class="text-sm text-slate-500 dark:text-slate-400"
                                v-if="billingAddress.line1"
                            >
                                {{ billingAddress.line1
                                }}{{
                                    billingAddress.city
                                        ? ', ' +
                                          billingAddress.city
                                        : ''
                                }}{{
                                    billingAddress.state
                                        ? ', ' +
                                          billingAddress.state
                                        : ''
                                }}
                                {{ billingAddress.postal_code }}
                            </p>
                        </template>
                        <p
                            v-else
                            class="text-sm text-slate-500 dark:text-slate-400"
                        >
                            Not set
                        </p>
                    </div>
                    <button
                        class="p-2.5 border border-slate-200 dark:border-slate-700 rounded shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40 transition-colors"
                        @click="$emit('edit-address')"
                    >
                        <Icon
                            icon="edit"
                            class="w-5 h-5 text-slate-500 dark:text-slate-400"
                        />
                    </button>
                </div>

                <!-- Billing Email -->
                <div
                    class="flex items-start justify-between pt-2"
                >
                    <div class="flex flex-col gap-1">
                        <p
                            class="text-sm font-medium text-slate-900 dark:text-slate-100"
                        >
                            Billing email
                        </p>
                        <p class="text-sm text-slate-500 dark:text-slate-400">
                            {{ billingEmail || 'Not set' }}
                        </p>
                    </div>
                    <button
                        class="p-2.5 border border-slate-200 dark:border-slate-700 rounded shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40 transition-colors"
                        @click="$emit('edit-email')"
                    >
                        <Icon
                            icon="edit"
                            class="w-5 h-5 text-slate-500 dark:text-slate-400"
                        />
                    </button>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    paymentMethods: {
        type: Array,
        default: () => [],
    },
    defaultPaymentMethodId: {
        type: String,
        default: null,
    },
    billingAddress: {
        type: Object,
        default: () => ({}),
    },
    billingEmail: {
        type: String,
        default: '',
    },
});

defineEmits([
    'add-card',
    'edit-card',
    'delete-card',
    'set-default',
    'edit-address',
    'edit-email',
]);

const openPaymentMenuId = ref(null);

const cardBrandLabel = (brand) => {
    if (!brand) return 'Card';
    const labels = {
        visa: 'Visa',
        mastercard: 'Mastercard',
        amex: 'Amex',
        discover: 'Discover',
        diners: 'Diners',
        jcb: 'JCB',
        unionpay: 'UnionPay',
    };
    return (
        labels[brand.toLowerCase()] ||
        brand.charAt(0).toUpperCase() + brand.slice(1)
    );
};

const togglePaymentMenu = (methodId) => {
    openPaymentMenuId.value =
        openPaymentMenuId.value === methodId ? null : methodId;
};

const handleClickOutside = (e) => {
    if (openPaymentMenuId.value && !e.target.closest('[data-payment-menu]')) {
        openPaymentMenuId.value = null;
    }
};

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});
</script>
